import { __decorate } from "tslib";
import cloneDeep from "lodash-es/cloneDeep";
import { hideProp } from "../../../tools/hideProp";
import isObjectLike from "lodash-es/isObjectLike";
import { State } from 'vue-class-state';
import Vue from 'vue';
import { hasOwn } from "../../../tools/hasOwn";
import { message } from 'ant-design-vue';
import { AsyncCatch } from "../../../tools/decorator/AsyncCatch";
import isShowMessageSchema from "../param/isShowMessageSchema";
import getActionResultDesc from "../param/getResultDesc";
import tsCode from "../param/tsCode";
import { isObject } from "lodash-es";
import { requestParamSchema } from "../param/requestParamSchema";
/**
 * 把列表的每一项数据进行代理，生成一个可进行表单操作（CRUD）的State
 */
export class ListItemState {
  constructor(rawData, listState) {
    this.data = rawData;
    this.oldData = cloneDeep(rawData);
    hideProp(this, 'collection', {
      get() {
        return listState.collection;
      }
    });
    hideProp(this, 'listState', {
      get() {
        return listState;
      }
    });
  }
  async $save(isShowMessage = true, params) {
    if (this.data.$isFromCopy) {
      return await this.$create(isShowMessage, params);
    }
    return await this.$update(isShowMessage, params);
  }
  async $create(isShowMessage = true, params) {
    const hasParams = isObject(params);
    const res = await this.collection.create(hasParams ? params : {
      body: this.data
    }, {
      raw: hasParams
    });
    const {
      body
    } = res;
    return this.listState.checkSucc(body, {
      cgiName: 'create',
      success: () => {
        let id;
        if (isObjectLike(body.data)) {
          this.updateState(body.data);
          id = this.getId();
        } else {
          console.warn('新建接口没有返回新建后的数据');
        }
        this.data.$isFromCopy = false;
        if (isShowMessage) {
          message.success(`${id ? `[${id}] ` : ''}新建成功`);
        }
      },
      showError: isShowMessage
    });
  }
  async $update(isShowMessage = true, params) {
    const hasParams = isObject(params);
    const id = hasParams ? '' : this.getId();
    const res = await this.collection.update(hasParams ? params : {
      query: {
        id // 获取id
      },
      body: this.data
    }, {
      oldData: this.oldData,
      raw: hasParams
    });
    const {
      body
    } = res;
    return this.listState.checkSucc(body, {
      cgiName: 'update',
      success: () => {
        if (isShowMessage) {
          message.success(`${id ? `[${id}]` : ''} 更新成功`);
        }
        if (isObjectLike(body.data)) {
          this.updateState(body.data);
        } else {
          console.warn('接口没有返回更新后的数据');
        }
      },
      showError: isShowMessage
    });
  }
  async $delete(isShowMessage = true, params) {
    if (this.data.$isFromCopy) {
      this.listState.removeItem(this.data);
      return {
        success: true
      };
    }
    const hasParams = isObject(params);
    let res;
    let id;
    if (hasParams) {
      res = await this.collection.delete(params, {
        oldData: this.oldData,
        raw: true
      });
    } else {
      id = this.getId();
      if (!id) {
        return {
          success: false,
          message: '找不到主键'
        };
      }
      res = await this.collection.delete({
        query: {
          id // 获取id
        },
        body: {
          id
        }
      }, {
        oldData: this.oldData
      });
    }
    const {
      body
    } = res;
    return this.listState.checkSucc(body, {
      cgiName: 'delete',
      success: () => {
        // wuji的格式
        if (typeof res.body.data === 'boolean') {
          if (!res.body.data) {
            message.error(`${id ? `[${id}]` : ''} 删除失败`);
            return;
          }
        }
        this.listState.removeItem(this.data);
        if (isShowMessage) {
          message.success(`${id ? `[${id}]` : ''} 删除成功`);
        }
      },
      showError: isShowMessage
    });
  }
  async $copy(options = {
    push: true,
    deleteId: true
  }) {
    const clone = await this.collection.copyEntity(this.data);
    if (options.deleteId) {
      const key = this.collection.getEntityPrimaryKey();
      if (key) {
        delete clone[key];
      }
    }
    const state = createListItemState(clone, this.listState, {
      isFromCopy: true
    });
    if (options.push) {
      this.listState.listData.push(state);
    }
    return {
      data: state,
      success: true
    };
  }
  updateState(newData) {
    this.oldData = cloneDeep(newData);
    const {
      data
    } = this;
    // 把多余字段删了 Object.keys不会遍历不可遍历的属性
    Object.keys(data).forEach(key => {
      if (!hasOwn(newData, key)) {
        Vue.delete(data, key);
      }
    });
    // 更新字段
    Object.keys(newData).forEach(key => {
      Vue.set(data, key, newData[key]);
    });
  }
  getId() {
    const key = this.collection.getEntityPrimaryKey();
    if (!key) {
      message.error('获取主键失败，请配置数据源实体主键');
    }
    return key ? this.data[key] : null;
  }
}
__decorate([State], ListItemState.prototype, "data", void 0);
__decorate([AsyncCatch()], ListItemState.prototype, "$save", null);
__decorate([AsyncCatch()], ListItemState.prototype, "$create", null);
__decorate([AsyncCatch()], ListItemState.prototype, "$update", null);
__decorate([AsyncCatch()], ListItemState.prototype, "$delete", null);
const commonResultDesc = getActionResultDesc('接口返回的数据');
function getArgsFunc(cgiName) {
  return listState => {
    let schema;
    try {
      schema = listState.getCgiParamSchema(cgiName) || {};
    } catch (error) {
      console.error('getCgiParamSchema fail', error);
    }
    return [isShowMessageSchema, {
      ...schema,
      ...requestParamSchema
    }];
  };
}
const listItemMethods = [{
  id: '$save',
  title: '保存(新建/更新)',
  cgiDeps: ['create', 'update'],
  args: getArgsFunc('update'),
  returnValue: commonResultDesc
}, {
  id: '$create',
  title: '新建',
  cgiDeps: ['create'],
  args: getArgsFunc('create'),
  returnValue: commonResultDesc
}, {
  id: '$update',
  title: '更新',
  cgiDeps: ['update'],
  args: getArgsFunc('update'),
  returnValue: commonResultDesc
}, {
  id: '$delete',
  title: '删除',
  cgiDeps: ['delete'],
  args: getArgsFunc('delete'),
  returnValue: commonResultDesc
}, {
  id: '$copy',
  title: '复制',
  cgiDeps: [],
  args: () => [{
    id: 'copyOption',
    title: 'copy选项',
    type: 'object',
    description: tsCode(`
{
  push: boolean // 是否添加到listData数组里面,默认为true
  deleteId: boolean // 是否删除数据中的主键id,默认为true
}
`),
    required: false,
    fields: [{
      id: 'push',
      type: 'boolean'
    }]
  }],
  returnValue: getActionResultDesc('复制的数据')
}];
const listItemStateKey = '$listItemState';
export function createListItemState(rawData, listState, options = {
  isFromCopy: false
}) {
  if (typeof rawData !== 'object' && rawData !== null) {
    return rawData;
  }
  const scopeState = new ListItemState(rawData, listState);
  listItemMethods === null || listItemMethods === void 0 || listItemMethods.map(item => item.id).forEach(funcName => {
    const method = scopeState[funcName].bind(scopeState);
    if (typeof method !== 'function') return;
    hideProp(rawData, funcName, {
      get() {
        return method;
      }
    });
  });
  hideProp(rawData, listItemStateKey, {
    get() {
      return scopeState;
    }
  });
  hideProp(rawData, '$isFromCopy', {
    value: options.isFromCopy,
    writable: true
  });
  return rawData;
}
export function getListItemStateMethods(listState) {
  const cgis = listState.getSupportedCgiMap();
  const methods = listItemMethods.filter(item => item.cgiDeps.every(name => cgis[name])).map(item => {
    var _item$args;
    const method = {
      id: item.id,
      title: item.title,
      arguments: (_item$args = item.args) === null || _item$args === void 0 ? void 0 : _item$args.call(item, listState),
      returnValue: item.returnValue
    };
    return method;
  });
  return methods;
}
export function isListItemState(data) {
  return (data === null || data === void 0 ? void 0 : data[listItemStateKey]) instanceof ListItemState;
}
//# sourceMappingURL=ListItemState.js.map
